import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, BarChart3, ArrowDown, Crown, Shield, Zap, MessageSquare, FileText, Mic, Loader2 } from 'lucide-react';
import { analyticsAPI } from '../lib/api';
import { toast } from 'sonner';

export default function AnalyticsPage() {
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [featureUsage, setFeatureUsage] = useState(null);
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [ov, fn, fu, us] = await Promise.allSettled([
          analyticsAPI.overview(),
          analyticsAPI.funnel(),
          analyticsAPI.featureUsage(),
          analyticsAPI.users(0, 20),
        ]);
        if (ov.status === 'fulfilled') setOverview(ov.value.data);
        if (fn.status === 'fulfilled') setFunnel(fn.value.data);
        if (fu.status === 'fulfilled') setFeatureUsage(fu.value.data);
        if (us.status === 'fulfilled') setUsers(us.value.data);

        if (ov.status === 'rejected' && ov.reason?.response?.status === 403) {
          setError('Admin access required to view analytics.');
        }
      } catch {
        setError('Failed to load analytics.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#0D9488' }} />
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center h-64" data-testid="analytics-error">
      <div className="text-center">
        <Shield className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--t-text-muted)' }} />
        <p className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>{error}</p>
        <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>Contact an administrator for access.</p>
      </div>
    </div>
  );

  const stats = overview ? [
    { label: 'Total Users', value: overview.users.total, icon: Users, color: '#0D9488' },
    { label: 'New This Week', value: overview.users.new_this_week, icon: TrendingUp, color: '#3B82F6' },
    { label: 'Pro Users', value: overview.subscriptions.pro, icon: Crown, color: '#D69E2E' },
    { label: 'Active Sessions', value: overview.engagement.active_sessions, icon: Zap, color: '#805AD5' },
  ] : [];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-xl font-bold" style={{ color: 'var(--t-text-heading)' }}>Platform Analytics</h1>
        <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>Real-time metrics and insights</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }} className="nx-card p-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${s.color}15` }}>
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
              </div>
            </div>
            <p className="text-2xl font-bold" style={{ color: 'var(--t-text-heading)' }}>{s.value}</p>
            <p className="text-[10px] font-medium" style={{ color: 'var(--t-text-muted)' }}>{s.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Conversion Funnel */}
        {funnel && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="nx-card p-5" data-testid="conversion-funnel">
            <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--t-text-heading)' }}>Conversion Funnel</h2>
            <div className="space-y-2">
              {funnel.funnel.map((stage, i) => (
                <div key={stage.stage}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium" style={{ color: 'var(--t-text-body)' }}>{stage.stage}</span>
                    <span className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>{stage.count} ({stage.pct}%)</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--t-bg-section)' }}>
                    <div className="h-full rounded-full transition-all" style={{
                      width: `${stage.pct}%`,
                      background: ['#0D9488', '#14B8A6', '#3B82F6', '#805AD5', '#D69E2E'][i] || '#0D9488',
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Feature Usage */}
        {featureUsage && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
            className="nx-card p-5" data-testid="feature-usage">
            <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--t-text-heading)' }}>Feature Usage</h2>
            <div className="space-y-3">
              {featureUsage.features.map((f) => {
                const icons = { 'Mock Interviews': Mic, 'Resume Saves': FileText, 'Community Posts': MessageSquare };
                const Icon = icons[f.name] || BarChart3;
                return (
                  <div key={f.name} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--t-bg-section)' }}>
                      <Icon className="w-4 h-4" style={{ color: 'var(--t-text-muted)' }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold" style={{ color: 'var(--t-text-heading)' }}>{f.name}</p>
                    </div>
                    <span className="text-sm font-bold" style={{ color: '#0D9488' }}>{f.usage_count}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>

      {/* Subscription Breakdown */}
      {overview && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="nx-card p-5" data-testid="subscription-breakdown">
          <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--t-text-heading)' }}>Subscription Breakdown</h2>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Free', value: overview.subscriptions.free, color: '#718096', icon: Zap },
              { label: 'Pro', value: overview.subscriptions.pro, color: '#0D9488', icon: Crown },
              { label: 'Enterprise', value: overview.subscriptions.enterprise, color: '#3B82F6', icon: Shield },
            ].map(s => (
              <div key={s.label} className="text-center p-3 rounded-xl" style={{ background: `${s.color}08`, border: `1px solid ${s.color}20` }}>
                <s.icon className="w-5 h-5 mx-auto mb-1" style={{ color: s.color }} />
                <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-[10px] font-medium" style={{ color: 'var(--t-text-muted)' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Users */}
      {users && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
          className="nx-card p-5" data-testid="recent-users">
          <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--t-text-heading)' }}>Recent Users</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--t-border)' }}>
                  {['Name', 'Email', 'Plan', 'Sessions', 'Joined'].map(h => (
                    <th key={h} className="text-left py-2 font-semibold" style={{ color: 'var(--t-text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.users.slice(0, 15).map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--t-border-subtle)' }}>
                    <td className="py-2 font-semibold" style={{ color: 'var(--t-text-heading)' }}>{u.name}</td>
                    <td style={{ color: 'var(--t-text-body)' }}>{u.email}</td>
                    <td>
                      <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                        style={{
                          background: u.plan === 'pro' ? 'rgba(13,148,136,0.15)' : u.plan === 'enterprise' ? 'rgba(59,130,246,0.15)' : 'var(--t-bg-section)',
                          color: u.plan === 'pro' ? '#0D9488' : u.plan === 'enterprise' ? '#3B82F6' : 'var(--t-text-muted)',
                        }}>
                        {u.plan}
                      </span>
                    </td>
                    <td style={{ color: 'var(--t-text-body)' }}>{u.sessions}</td>
                    <td style={{ color: 'var(--t-text-muted)' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  );
}
